import {
  addBerlinDays,
  berlinCalendarDate,
  berlinDateTime,
  berlinMonth,
  berlinWeek,
  parseBerlinDate,
  type IntranetFeedItem,
} from '@gb-ticket/shared-types';

import { toDatetimeLocalValue } from '@/lib/format';
import { coverSrc } from '@/products/intranet/features/intranet/cover-src';
import { INTRANET_CHANNEL_COMPOSE_PATH } from '@/products/intranet/intranet-paths';

export type EditorialCalendarView = 'week' | 'month' | 'agenda';
export type EditorialCalendarSpan = 'week' | 'month';

export type EditorialCalendarQuery = {
  view: EditorialCalendarView;
  span: EditorialCalendarSpan;
  date: string;
  categoryId: string | null;
};

const WEEKDAY_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const;
const WEEKDAY_LONG = [
  'Montag',
  'Dienstag',
  'Mittwoch',
  'Donnerstag',
  'Freitag',
  'Samstag',
  'Sonntag',
] as const;
const MONTH_LONG = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
] as const;

function partsOf(date: string) {
  return parseBerlinDate(date);
}

function weekdayIndex(date: string): number {
  const parts = partsOf(date);
  if (!parts) {
    return 0;
  }
  const sunday0 = new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay();
  return sunday0 === 0 ? 6 : sunday0 - 1;
}

export function editorialToday(now = new Date()): string {
  return berlinCalendarDate(now);
}

export function readEditorialCalendar(
  params: URLSearchParams,
  today: string,
): { query: EditorialCalendarQuery; dateWasInvalid: boolean } {
  const rawDate = params.get('date');
  const dateWasInvalid = rawDate === null || parseBerlinDate(rawDate) === null;
  const viewRaw = params.get('view');
  const spanRaw = params.get('span');
  const view: EditorialCalendarView =
    viewRaw === 'month' || viewRaw === 'agenda' || viewRaw === 'week' ? viewRaw : 'week';
  const requestedSpan: EditorialCalendarSpan = spanRaw === 'month' ? 'month' : 'week';
  const span: EditorialCalendarSpan =
    view === 'month' ? 'month' : view === 'week' ? 'week' : requestedSpan;
  const category = params.get('category');
  return {
    query: {
      view,
      span,
      date: dateWasInvalid ? today : rawDate,
      categoryId: category && category.length > 0 ? category : null,
    },
    dateWasInvalid,
  };
}

export function editorialCalendarSearch(query: EditorialCalendarQuery): string {
  const params = new URLSearchParams();
  params.set('view', query.view);
  params.set('span', query.span);
  params.set('date', query.date);
  if (query.categoryId) {
    params.set('category', query.categoryId);
  }
  return params.toString();
}

export function editorialVisibleSpan(
  query: EditorialCalendarQuery,
  desktop: boolean,
): EditorialCalendarSpan {
  if (!desktop) {
    return 'week';
  }
  if (query.view === 'month') {
    return 'month';
  }
  if (query.view === 'agenda') {
    return query.span;
  }
  return 'week';
}

export function editorialVisibleView(
  query: EditorialCalendarQuery,
  desktop: boolean,
): EditorialCalendarView {
  if (!desktop) {
    return 'agenda';
  }
  return query.view;
}

export function editorialRange(
  anchor: string,
  span: EditorialCalendarSpan,
): { from: string; to: string } {
  if (span === 'month') {
    const month = berlinMonth(anchor);
    return month ? { from: month.start, to: month.end } : { from: anchor, to: anchor };
  }
  const week = berlinWeek(anchor);
  return week ? { from: week.start, to: week.end } : { from: anchor, to: anchor };
}

export function shiftEditorialAnchor(
  anchor: string,
  span: EditorialCalendarSpan,
  direction: -1 | 1,
): string {
  if (span === 'week') {
    return addBerlinDays(anchor, direction * 7) ?? anchor;
  }
  const parts = partsOf(anchor);
  if (!parts) {
    return anchor;
  }
  const month = parts.month + direction;
  const year = parts.year + (month < 1 ? -1 : month > 12 ? 1 : 0);
  const nextMonth = month < 1 ? 12 : month > 12 ? 1 : month;
  return `${year}-${String(nextMonth).padStart(2, '0')}-01`;
}

export function editorialMonthAnchor(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}-01`;
}

export function editorialMonthChoices(anchor: string): { value: string; label: string }[] {
  const parts = partsOf(anchor);
  if (!parts) {
    return [];
  }
  const previous = {
    value: editorialMonthAnchor(parts.year - 1, parts.month),
    label: String(parts.year - 1),
  };
  const next = {
    value: editorialMonthAnchor(parts.year + 1, parts.month),
    label: String(parts.year + 1),
  };
  const months = MONTH_LONG.map((label, index) => ({
    value: editorialMonthAnchor(parts.year, index + 1),
    label: `${label} ${parts.year}`,
  }));
  return [previous, next, ...months];
}

export function editorialMonthValue(anchor: string): string {
  const parts = partsOf(anchor);
  if (!parts) {
    return anchor;
  }
  return editorialMonthAnchor(parts.year, parts.month);
}

export function editorialMonthLabel(anchor: string): string {
  const parts = partsOf(anchor);
  if (!parts) {
    return anchor;
  }
  return `${MONTH_LONG[parts.month - 1]} ${parts.year}`;
}

export function editorialWeekLabel(anchor: string): string | null {
  const week = berlinWeek(anchor);
  return week ? `Woche ${week.isoWeek}` : null;
}

export function editorialColumnLabel(date: string): string {
  const parts = partsOf(date);
  if (!parts) {
    return date;
  }
  return `${WEEKDAY_SHORT[weekdayIndex(date)]}. ${parts.day}`;
}

export function editorialDayHeading(date: string): string {
  const parts = partsOf(date);
  if (!parts) {
    return date;
  }
  return `${WEEKDAY_LONG[weekdayIndex(date)]}, ${parts.day}. ${MONTH_LONG[parts.month - 1]} ${parts.year}`;
}

export function editorialTimeLabel(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '00';
  const hour = value('hour') === '24' ? '00' : value('hour');
  return `${hour}:${value('minute')}`;
}

export function contributionCountLabel(count: number): string | null {
  if (count <= 0) {
    return null;
  }
  return count === 1 ? '1 Beitrag' : `${count} Beiträge`;
}

export function editorialPlacementIso(item: {
  isDraft: boolean;
  publishedAt: string | null;
  publishAt?: string | null;
  createdAt?: string | null;
}): string | null {
  if (!item.isDraft) {
    return item.publishedAt;
  }
  return item.publishAt ?? item.createdAt ?? null;
}

export function showsEditorialHighlight(item: {
  isDraft: boolean;
  pinned: boolean;
  highlightSpan?: string | null;
}): boolean {
  if (item.pinned) {
    return true;
  }
  return item.isDraft && Boolean(item.highlightSpan);
}

export function editorialCoverUrl(item: {
  coverUrl: string | null;
  coverMedia?: 'image' | 'video' | null;
}): string | null {
  if (!item.coverUrl || item.coverMedia === 'video') {
    return null;
  }
  return coverSrc(item);
}

export function editorialDays<T extends { id: string }>(
  items: readonly T[],
  from: string,
  to: string,
  placement: (item: T) => string | null,
): { date: string; items: T[] }[] {
  const days: { date: string; items: T[] }[] = [];
  let cursor: string | null = from;
  const guard = new Set<string>();
  while (cursor && cursor <= to && !guard.has(cursor)) {
    guard.add(cursor);
    days.push({ date: cursor, items: [] });
    cursor = addBerlinDays(cursor, 1);
  }
  const byDate = new Map(days.map((day) => [day.date, day]));
  for (const item of items) {
    const instant = placement(item);
    if (!instant) {
      continue;
    }
    const parsed = new Date(instant);
    if (Number.isNaN(parsed.getTime())) {
      continue;
    }
    byDate.get(berlinCalendarDate(parsed))?.items.push(item);
  }
  return days;
}

export function editorialComposePath(input: {
  date: string | null;
  categoryId: string | null;
  now: Date;
}): string {
  const params = new URLSearchParams();
  if (input.categoryId) {
    params.set('category', input.categoryId);
  }
  if (input.date && parseBerlinDate(input.date)) {
    const at = berlinDateTime(input.date, 8, 0);
    if (at && at.getTime() > input.now.getTime()) {
      params.set('publishAt', at.toISOString());
    } else {
      params.set('day', input.date);
    }
  }
  const query = params.toString();
  return query.length > 0
    ? `${INTRANET_CHANNEL_COMPOSE_PATH}?${query}`
    : INTRANET_CHANNEL_COMPOSE_PATH;
}

/** Fills „Veröffentlichen am“ from a valid `publishAt`. A `day` alone leaves the field empty. */
export function publishAtFieldFromQuery(publishAt: string | null): string {
  if (!publishAt) {
    return '';
  }
  const parsed = new Date(publishAt);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return toDatetimeLocalValue(parsed.toISOString());
}

export function editorialPlanLabel(date: string): string {
  return `Beitrag planen am ${editorialDayHeading(date)}`;
}

export type EditorialCalendarItem = IntranetFeedItem;
